import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { Progress } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { CardHeader } from "../../components/shared";
import { device } from "../../lib/breakpoints";
import { format } from "date-fns";

const CollectionProgress = ({
  collectionRate = 0,
  paidCount = 0,
  totalCount = 0,
  onMonthChange,
  currentMonth = new Date(),
  monthsWithPayments = [],
}) => {
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [transition, setTransition] = useState(null);
  const transitionTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
    },
    []
  );

  const changeMonth = (newMonth, direction) => {
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
    }

    setTransition({
      id: Date.now(),
      direction,
      previousMonth: selectedMonth,
      previousCollectionRate: collectionRate,
      previousPaidCount: paidCount,
      previousTotalCount: totalCount,
    });
    setSelectedMonth(newMonth);
    onMonthChange?.(newMonth);

    transitionTimerRef.current = setTimeout(() => {
      setTransition(null);
      transitionTimerRef.current = null;
    }, 240);
  };

  const handlePreviousMonth = () => {
    const currentIndex = monthsWithPayments.findIndex(
      (month) =>
        month.getFullYear() === selectedMonth.getFullYear() &&
        month.getMonth() === selectedMonth.getMonth()
    );

    if (currentIndex > 0) {
      const newMonth = monthsWithPayments[currentIndex - 1];
      changeMonth(newMonth, "previous");
    }
  };

  const handleNextMonth = () => {
    const currentIndex = monthsWithPayments.findIndex(
      (month) =>
        month.getFullYear() === selectedMonth.getFullYear() &&
        month.getMonth() === selectedMonth.getMonth()
    );

    if (currentIndex < monthsWithPayments.length - 1) {
      const newMonth = monthsWithPayments[currentIndex + 1];
      changeMonth(newMonth, "next");
    }
  };

  // Custom month abbreviations for better readability
  const monthAbbreviations = {
    January: "Jan",
    February: "Feb",
    March: "Mar",
    April: "Apr",
    May: "May",
    June: "Jun",
    July: "Jul",
    August: "Aug",
    September: "Sept",
    October: "Oct",
    November: "Nov",
    December: "Dec",
  };

  const getMonthLabels = (date) => {
    const monthName = date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    const month = format(date, "MMMM");
    const year = format(date, "yyyy");
    const abbreviatedMonth = monthAbbreviations[month] || month.substring(0, 3);
    return {
      monthName,
      monthNameAbbreviated: `${abbreviatedMonth} '${year.slice(-2)}`,
    };
  };

  const isCurrentCalendarMonth = (date) => {
    const now = new Date();
    return (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth()
    );
  };

  const renderMonthTitle = (date) => {
    const { monthName, monthNameAbbreviated } = getMonthLabels(date);
    return (
      <TitleText>
        <span className="desktop-month">
          {monthName} Collection Progress
        </span>
        <span className="mobile-month">
          {monthNameAbbreviated} Collection Progress
        </span>
        {isCurrentCalendarMonth(date) && <CurrentMonthIcon />}
      </TitleText>
    );
  };

  const renderProgress = (rate, paid, total) => (
    <ProgressContent>
      <ProgressNumber>{rate}%</ProgressNumber>
      <Progress
        percent={rate}
        status="active"
        strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }}
        strokeWidth={16}
        showInfo={false}
        trailColor="#f0f0f0"
      />
      <ProgressSubtext>
        {paid} of {total} payments collected
      </ProgressSubtext>
    </ProgressContent>
  );

  // Check if we can navigate to previous/next month
  const currentIndex = monthsWithPayments.findIndex(
    (month) =>
      month.getFullYear() === selectedMonth.getFullYear() &&
      month.getMonth() === selectedMonth.getMonth()
  );

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < monthsWithPayments.length - 1;
  return (
    <CardHeader
      title={
        <HeaderContent>
          <NavigationButton
            onClick={handlePreviousMonth}
            disabled={!canGoPrevious}
          >
            <LeftOutlined style={{ color: "white", fontSize: "20px" }} />
          </NavigationButton>
          <TitleContainer>
            {transition && (
              <TitleSlide
                key={`title-out-${transition.id}`}
                $phase="outgoing"
                $direction={transition.direction}
              >
                {renderMonthTitle(transition.previousMonth)}
              </TitleSlide>
            )}
            <TitleSlide
              key={`title-in-${transition?.id || "idle"}`}
              $phase={transition ? "incoming" : "idle"}
              $direction={transition?.direction}
            >
              {renderMonthTitle(selectedMonth)}
            </TitleSlide>
          </TitleContainer>
          <NavigationButton onClick={handleNextMonth} disabled={!canGoNext}>
            <RightOutlined style={{ color: "white", fontSize: "20px" }} />
          </NavigationButton>
        </HeaderContent>
      }
      subtitle="Track your monthly rent collection progress"
    >
      <ProgressContainer>
        {transition && (
          <ProgressSlide
            key={`progress-out-${transition.id}`}
            $phase="outgoing"
            $direction={transition.direction}
          >
            {renderProgress(
              transition.previousCollectionRate,
              transition.previousPaidCount,
              transition.previousTotalCount
            )}
          </ProgressSlide>
        )}
        <ProgressSlide
          key={`progress-in-${transition?.id || "idle"}`}
          $phase={transition ? "incoming" : "idle"}
          $direction={transition?.direction}
        >
          {renderProgress(collectionRate, paidCount, totalCount)}
        </ProgressSlide>
      </ProgressContainer>
    </CardHeader>
  );
};

export default CollectionProgress;

// Styled Components (scoped to this component)
const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-18px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-18px); }
`;

const slideOutToRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(18px); }
`;

const inFromLeftAnimation = css`
  animation: ${slideInFromLeft} 240ms ease-out both;
`;
const inFromRightAnimation = css`
  animation: ${slideInFromRight} 240ms ease-out both;
`;
const outToLeftAnimation = css`
  animation: ${slideOutToLeft} 240ms ease-out both;
`;
const outToRightAnimation = css`
  animation: ${slideOutToRight} 240ms ease-out both;
`;

const carouselAnimation = (phase, direction) => {
  if (phase === "idle") {
    return css`
      animation: none;
    `;
  }
  if (phase === "incoming") {
    return direction === "previous"
      ? inFromLeftAnimation
      : inFromRightAnimation;
  }
  return direction === "previous" ? outToRightAnimation : outToLeftAnimation;
};

const ProgressContainer = styled.div`
  display: grid;
  place-items: center;
  text-align: center;
  height: 100%;
  padding: 1rem 0;
  overflow: hidden;
`;

const ProgressSlide = styled.div`
  grid-area: 1 / 1;
  display: flex;
  justify-content: center;
  width: 100%;
  ${(props) => carouselAnimation(props.$phase, props.$direction)}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ProgressContent = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProgressNumber = styled.div`
  text-align: center;
  font-size: 3.5rem;
  color: var(--color-primary);
  font-weight: 800;
  font-family: var(--font-primary);
  margin: 0;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(44, 95, 45, 0.1);

  @media ${device.mobile} {
    font-size: 2.8rem;
  }
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 1.3rem;
  color: var(--color-text-primary);
  font-weight: 700;
  font-family: var(--font-primary);
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
`;

const ProgressSubtext = styled.div`
  text-align: center;
  font-size: 1.1rem;
  color: var(--color-text-light);
  font-weight: 500;
  font-family: var(--font-secondary);
  margin: 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;

  span {
    filter: brightness(1) invert(0);
  }
`;

const TitleContainer = styled.div`
  display: grid;
  place-items: center;
  flex: 1;
  overflow: hidden;
`;

const TitleSlide = styled.div`
  grid-area: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => carouselAnimation(props.$phase, props.$direction)}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TitleText = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
  color: white;

  .desktop-month {
    display: inline;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;

    @media ${device.mobile} {
      display: none;
    }
  }

  .mobile-month {
    display: none;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;

    @media ${device.mobile} {
      display: inline;
    }
  }
`;

const CurrentMonthIcon = styled.div`
  width: 8px;
  height: 8px;
  background: #52c41a;
  border-radius: 50%;
  animation: pulse 2s infinite;
  flex-shrink: 0;

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(82, 196, 26, 1);
    }
    70% {
      box-shadow: 0 0 0 4px rgba(82, 196, 26, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(82, 196, 26, 0);
    }
  }
`;

const NavigationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.1);
  }

  .anticon {
    font-size: 20px;
    color: white !important;
  }

  svg {
    color: white !important;
    fill: white !important;
  }

  /* More specific selectors to override Ant Design */
  .anticon-left,
  .anticon-right {
    color: white !important;
  }

  .anticon svg {
    color: white !important;
    fill: white !important;
  }

  /* Target the specific icon classes */
  [class*="anticon-left"],
  [class*="anticon-right"] {
    color: white !important;
  }

  [class*="anticon-left"] svg,
  [class*="anticon-right"] svg {
    color: white !important;
    fill: white !important;
  }
`;
